import { callFirebaseFunction } from 'boot/firebase';
import { INTEGRATION_REFERENCES_NOTIFICATIONS_TYPES } from 'src/constants/notifications';
import IntegrationCacheDB from 'src/fb/integrationCache-db';
import RealTimeNotificationsDB from 'src/fb/realTimeNotifications-db';
import { auth as useAuthStore } from 'src/store/auth';
import { network as useNetworkStore } from 'src/store/network';
import { bindDataBaseStore, unbindDataBaseStore } from 'src/utils/FirestoreUtils';

/**
 * A large clinic's `integrationCache` can hold tens of thousands of documents; reading it all in
 * one `readAll()` makes one request whose response is big enough, and slow enough to arrive, to
 * risk timing out or tripping Firestore's read quota on a slow connection. Not a measured figure —
 * a starting default to tune against a real slow-network repro on staging.
 */
const REFERENCES_PAGE_SIZE = 10000;

/**
 * Reads a clinic's integration references a page at a time instead of in one large read, so no
 * single request is big or slow enough to risk the problem above. Pages are read sequentially
 * because each one needs the previous page's cursor.
 *
 * @param {'staff' | 'client'} objectType
 * @param {string} clinicId
 * @return {Promise<Object[]>}
 */
const readAllReferencesPaginated = async (objectType, clinicId) => {
  const integrationCacheDb = new IntegrationCacheDB();
  const constraints = [
    ['clinic', '==', clinicId],
    ['type', '==', objectType],
  ];

  const references = [];
  let afterDoc = null;
  let isFirstPage = true;

  while (isFirstPage || afterDoc) {
    isFirstPage = false;

    // eslint-disable-next-line no-await-in-loop
    const page = await integrationCacheDb.readPage(constraints, REFERENCES_PAGE_SIZE, afterDoc);
    references.push(...page.items);
    afterDoc = page.items.length === REFERENCES_PAGE_SIZE ? page.lastDoc : null;
  }

  return references;
};

export default {
  setReferences(objectType, references, linkedNames) {
    this.referencesCache[objectType] = references;
    this.linkedNamesCache[objectType] = linkedNames;
  }, // setReferences()

  clearReferencesCache() {
    this.clinicId = null;
    this.referencesCache = {};
    this.linkedNamesCache = {};
    this.staleTypes = {};
    this.handledNotificationIds = {};
    // Not awaited elsewhere in the codebase either — any load already in flight for the old clinic
    // keeps running, but a new call for the same object type will start its own instead of joining it.
    this.pendingLoads = {};
  }, // clearReferencesCache()

  /**
   * Marks the cache as needing a refetch. Nothing is read here on purpose: a clinic can hold tens
   * of thousands of references, and the user may never open the linking dialog again this session.
   *
   * @param {'staff' | 'client' | null} [objectType] - all cached types when omitted
   */
  invalidateReferences(objectType = null) {
    if (objectType) {
      this.staleTypes[objectType] = true;
      return;
    }

    Object.keys(this.referencesCache).forEach((type) => {
      this.staleTypes[type] = true;
    });
  }, // invalidateReferences()

  /**
   * Returns the clinic's integration references, reading them from the database only when the
   * cache is missing, stale, or belongs to another clinic.
   *
   * @param {'staff' | 'client'} objectType
   * @param {Object} [options]
   * @param {boolean} [options.force] - refetch even when the cache is valid
   * @return {Promise<Object[]>}
   */
  async loadReferences(objectType, { force = false } = {}) {
    // A second call for the same object type made while one is still in flight (e.g. canceling and
    // reopening the link dialog) joins the same promise instead of starting a duplicate paginated read.
    // A forced call never joins one, though — Refresh has to get genuinely fresh data, not whatever an
    // unrelated pending load happens to resolve with.
    if (!force && this.pendingLoads[objectType]) {
      return this.pendingLoads[objectType];
    }

    const loadPromise = (async () => {
      const authStore = useAuthStore();
      const clinicId = authStore.userDetails?.clinic;

      if (!clinicId) {
        return [];
      }

      if (this.clinicId !== clinicId) {
        // Support access moves a user between clinics without a reload, and the signal below is
        // filtered by clinic, so it has to be rebound alongside the cache.
        this.unbindReferencesInvalidation();
        this.clearReferencesCache();
        this.clinicId = clinicId;
      }

      const needsReferences = force
        || !Array.isArray(this.referencesCache[objectType])
        || this.staleTypes[objectType];

      // The linked-names half is never cached: unlike the reference list it costs no client-side
      // Firestore reads (`getReferencesLinks` no longer touches `integrationCache`), and caching it
      // would leave a link made by another admin invisible for the rest of the session.
      if (!needsReferences) {
        const linkedNames = await callFirebaseFunction('getReferencesLinks', { referenceType: objectType })
          .then(({ data }) => data);

        if (this.clinicId !== clinicId || this.pendingLoads[objectType] !== loadPromise) {
          return [];
        }

        this.linkedNamesCache[objectType] = linkedNames;
        return this.referencesCache[objectType];
      }

      // Bound before reading, so a change landing mid-read still marks the cache stale.
      await this.bindReferencesInvalidation();

      // Cleared before the read starts rather than after it resolves, so an invalidation landing
      // while the read is in flight flips this back to true instead of being clobbered by a later
      // blind reset if the read fails.
      this.staleTypes[objectType] = false;

      let references;
      let linkedNames;

      try {
        [references, linkedNames] = await Promise.all([
          readAllReferencesPaginated(objectType, clinicId),
          callFirebaseFunction('getReferencesLinks', { referenceType: objectType })
            .then(({ data }) => data),
        ]);
      } catch (err) {
        // The read didn't happen, so the cache is still exactly as stale as the flag said before
        // it was cleared above. Restoring it keeps the next call from serving stale data as if it
        // were current — reasserting `true` is safe even if an invalidation landed in the meantime.
        // But only for this same load: a forced call for the same object type doesn't join this
        // one and can commit its own fresher result first, taking over `pendingLoads[objectType]`
        // — an older call failing afterwards must not mark that fresh result stale.
        if (this.clinicId === clinicId && this.pendingLoads[objectType] === loadPromise) {
          this.staleTypes[objectType] = true;
        }
        throw err;
      }

      if (this.clinicId !== clinicId || this.pendingLoads[objectType] !== loadPromise) {
        return [];
      }

      this.setReferences(objectType, references, linkedNames);

      return references;
    })();

    this.pendingLoads[objectType] = loadPromise;

    try {
      return await loadPromise;
    } finally {
      // A forced call can overwrite this entry while an unrelated earlier load for the same object
      // type is still running; only clear it if it still points at this call's own promise, or a
      // still-in-flight forced load's entry gets deleted out from under it by the older one finishing.
      if (this.pendingLoads[objectType] === loadPromise) {
        delete this.pendingLoads[objectType];
      }
    }
  }, // loadReferences()

  /**
   * Watches a signal collection instead of the references themselves. Listening to
   * `integrationCache` directly means streaming every reference the clinic has.
   */
  async bindReferencesInvalidation() {
    const networkStore = useNetworkStore();

    if (networkStore.offlineMode) {
      return;
    }

    // `staff` and `client` can load in parallel and both call this; joining the same in-flight bind
    // instead of setting `isInvalidationBound` before the `await` avoids a second caller starting a
    // paginated read before the listener is actually subscribed.
    if (this.pendingInvalidationBind) {
      await this.pendingInvalidationBind;
      return;
    }

    if (this.isInvalidationBound) {
      return;
    }

    const realTimeNotificationsDb = new RealTimeNotificationsDB();
    // Only notifications raised from now on matter — the cache is current as of this moment, and
    // the collection keeps every past notification.
    const ref = realTimeNotificationsDb.getQuery([
      ['clinic', '==', this.clinicId],
      ['context', '==', RealTimeNotificationsDB.context.INTEGRATION_REFERENCES],
      ['type', '==', INTEGRATION_REFERENCES_NOTIFICATIONS_TYPES.REFERENCES_UPDATED],
      ['createTimestamp', '>=', new Date()],
    ], 'createTimestamp', 'asc');

    this.pendingInvalidationBind = (async () => {
      try {
        await bindDataBaseStore(
          { store: this, property: 'invalidationNotifications', action: 'onReferencesInvalidated' },
          ref,
          [],
        );
        this.isInvalidationBound = true;
      } finally {
        this.pendingInvalidationBind = null;
      }
    })();

    await this.pendingInvalidationBind;
  }, // bindReferencesInvalidation()

  onReferencesInvalidated(notifications) {
    this.invalidationNotifications = notifications;

    // Each snapshot carries every notification still live (up to 4 hours old, see
    // `clearRealTimeNotifications`), not just a new one — without this guard, a fresh notification
    // for one object type would re-mark every earlier, already-handled type stale again too.
    notifications
      .filter(({ id }) => !this.handledNotificationIds[id])
      .forEach(({ id, referenceType }) => {
        this.handledNotificationIds[id] = true;
        this.invalidateReferences(referenceType || null);
      });
  }, // onReferencesInvalidated()

  unbindReferencesInvalidation() {
    if (!this.isInvalidationBound) {
      return null;
    }

    this.isInvalidationBound = false;

    return unbindDataBaseStore({ store: this, property: 'invalidationNotifications' }, []);
  }, // unbindReferencesInvalidation()

  logout() {
    this.unbindReferencesInvalidation();
    this.clearReferencesCache();
  }, // logout()
};
